import { call, put } from 'redux-saga/effects';
import { map } from 'lodash/fp';
import { toastr } from 'react-redux-toastr';
import * as Api from '../../../services/fbm-api';
import { sagaRoutineRequest } from '../../../helpers/saga';
import {
  createReservation as createReservationRoutine,
  updateReservation as updateReservationRoutine,
  unlinkReservation as unlinkReservationRoutine,
  createReservationOrders as createReservationOrdersRoutine,
  updateReservationOrders as updateReservationOrdersRoutine,
  fetchReservationForEdit as fetchReservationForEditRoutine,
  fetchReservationForView as fetchReservationForViewRoutine,
  fetchReservationPicklists as fetchReservationPicklistsRoutine,
  fetchReservationToday as fetchReservationTodayRoutine,
  fetchReservationUpcoming as fetchReservationUpcomingRoutine,
  fetchReservationPending as fetchReservationPendingRoutine,
  fetchReservationArchived as fetchReservationArchivedRoutine,
  updateReservationStatus as updateReservationStatusRoutine,
  fetchReservationExportUrl as fetchReservationExportUrlRoutine,
  fetchReservationUpcomingEvents as fetchReservationUpcomingEventsRoutine,
  fetchReservationAvailableEvent as fetchReservationAvailableEventRoutine,
  fetchReservationAvailableEvents as fetchReservationAvailableEventsRoutine,
  fetchReservationsOrderItems as fetchReservationsOrderItemsRoutine,
  fetchROItemOptions as fetchROItemOptionsRoutine,
  fetchReservationTrackingMethodOptions as fetchReservationTrackingMethodOptionsRoutine,
  fetchInventoryItemAvailabilityByLocation as fetchInventoryItemAvailabilityByLocationRoutine,
  fetchInventoryPackageAvailabilityByLocation as fetchInventoryPackageAvailabilityByLocationRoutine
} from './routines';
import { reservationPendingRemoveItem } from '../actions/pending';
import { reservationArchivedRemoveItem } from '../actions/archived';
import StatusLabels from '../../../constants/reservation/StatusLabels';
import {
  REJECT,
  APPROVE,
  CANCEL
} from '../../../constants/reservation/StatusActions';
import { reservationsViewPath } from '../../../routes/Paths';
import history from '../../../routes/history';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import redirectTo from '../../../helpers/redirectTo';
import {
  clearInventoryItemsAvailability,
  clearInventoryPackagesAvailability
} from '../../Inventory/actions';

export * from './ROItem';
export * from './ROPicklist';
export * from './reports';

export const createReservation = ({ payload: { body, redirectPath = '' } }) =>
  sagaRoutineRequest(createReservationRoutine, {
    request: () => call(Api.createReservation, { body }),
    *success({ id }) {
      yield toastr.success('Success', 'Reservation successfully created.');
      if (redirectPath) {
        redirectTo(redirectPath);
      } else {
        history.push(pagePathToUrl(reservationsViewPath, { id }));
      }
    }
  });

export const updateReservation = ({ payload: { id, body } }) =>
  sagaRoutineRequest(updateReservationRoutine, {
    request: () => call(Api.updateReservation, { id, body }),
    *success() {
      yield toastr.success('Success', 'Reservation successfully updated.');
      history.push(pagePathToUrl(reservationsViewPath, { id }));
    }
  });

export const unlinkReservation = ({ payload: { id, body } }) =>
  sagaRoutineRequest(unlinkReservationRoutine, {
    request: () =>
      call(Api.updateReservation, {
        id,
        body: { ...body, programs: map('id', body.programs), guestId: null }
      }),
    *success() {
      yield toastr.success('Success', 'Guest successfully unlinked.');
    }
  });

export const createReservationOrders = ({ payload: { id, body } }) =>
  sagaRoutineRequest(createReservationOrdersRoutine, {
    request: () => call(Api.createReservationOrders, { id, body }),
    *success() {
      yield toastr.success('Success', 'Orders successfully created.');
      history.push(pagePathToUrl(reservationsViewPath, { id }));
    }
  });

export const updateReservationOrders = ({ payload: { id, body } }) =>
  sagaRoutineRequest(updateReservationOrdersRoutine, {
    request: () => call(Api.updateReservationOrders, { id, body }),
    *success() {
      yield toastr.success('Success', 'Orders successfully updated.');
      history.push(pagePathToUrl(reservationsViewPath, { id }));
    }
  });

export const fetchReservationForEdit = ({ payload: id }) =>
  sagaRoutineRequest(fetchReservationForEditRoutine, {
    request: () => call(Api.fetchReservation, id)
  });

export const fetchReservationForView = ({ payload: id }) =>
  sagaRoutineRequest(fetchReservationForViewRoutine, {
    request: () => call(Api.fetchReservation, id)
  });

export const fetchReservationPicklists = ({ payload: id }) =>
  sagaRoutineRequest(fetchReservationPicklistsRoutine, {
    request: () => call(Api.fetchReservationPicklists, id)
  });

export const fetchReservationToday = ({ payload: qs }) =>
  sagaRoutineRequest(fetchReservationTodayRoutine, {
    request: () => call(Api.fetchReservationToday, { qs })
  });

export const fetchReservationUpcoming = ({ payload: qs }) =>
  sagaRoutineRequest(fetchReservationUpcomingRoutine, {
    request: () => call(Api.fetchReservationUpcoming, { qs })
  });

export const fetchReservationPending = ({ payload: qs }) =>
  sagaRoutineRequest(fetchReservationPendingRoutine, {
    request: () => call(Api.fetchReservationPending, { qs })
  });

export const fetchReservationUpcomingEvents = ({ payload: qs }) =>
  sagaRoutineRequest(fetchReservationUpcomingEventsRoutine, {
    request: () => call(Api.fetchOutreachesUpcomimg, { qs })
  });

export const fetchReservationAvailableEvent = ({ payload: id }) =>
  sagaRoutineRequest(fetchReservationAvailableEventRoutine, {
    request: () => call(Api.fetchOutreachSchedule, id)
  });

export const fetchReservationAvailableEvents = ({ payload: qs }) =>
  sagaRoutineRequest(fetchReservationAvailableEventsRoutine, {
    request: () => call(Api.fetchOutreachSchedules, { qs })
  });

export const fetchReservationsOrderItems = ({ payload: qs }) =>
  sagaRoutineRequest(fetchReservationsOrderItemsRoutine, {
    request: () => call(Api.fetchReservationsOrderItems, { qs })
  });

export const updateReservationStatus = ({
  payload: { id, body, statusAction, skipRedirect = false }
}) =>
  sagaRoutineRequest(updateReservationStatusRoutine, {
    request: () =>
      call(Api.updateReservationStatus, { id, body, statusAction }),
    *success({ status }) {
      if (statusAction === REJECT || statusAction === APPROVE) {
        yield put(reservationPendingRemoveItem(id));
      } else if (statusAction === CANCEL) {
        yield put(reservationArchivedRemoveItem(id));
        if (!skipRedirect) {
          yield put(clearInventoryItemsAvailability());
          yield put(clearInventoryPackagesAvailability());
        }
      }
      if (!skipRedirect) {
        history.push(pagePathToUrl(reservationsViewPath, { id }));
      }
      yield toastr.success('Success', `${StatusLabels[status]} Reservation.`);
    }
  });

export const fetchReservationTrackingMethodOptions = ({ payload: guestId }) =>
  sagaRoutineRequest(fetchReservationTrackingMethodOptionsRoutine, {
    request: () => call(Api.fetchUpcomingOutreachPrograms, guestId)
  });

export const fetchROItemOptions = () =>
  sagaRoutineRequest(fetchROItemOptionsRoutine, {
    request: () => call(Api.fetchROItemOptions)
  });

export const fetchReservationArchived = ({ payload: qs }) =>
  sagaRoutineRequest(fetchReservationArchivedRoutine, {
    request: () => call(Api.fetchReservationArchived, { qs })
  });

export const fetchReservationExportUrl = ({ payload: { type, options } }) =>
  sagaRoutineRequest(fetchReservationExportUrlRoutine, {
    request: () => call(Api.fetchReservationExportUrl, { type, options })
  });

export const fetchInventoryItemAvailabilityByLocation = ({ payload }) =>
  sagaRoutineRequest(fetchInventoryItemAvailabilityByLocationRoutine, {
    request: () => call(Api.fetchInventoryItemAvailabilityByLocation, payload)
  });

export const fetchInventoryPackageAvailabilityByLocation = ({ payload }) =>
  sagaRoutineRequest(fetchInventoryPackageAvailabilityByLocationRoutine, {
    request: () =>
      call(Api.fetchInventoryPackageAvailabilityByLocation, payload)
  });
